import { useCallback, useEffect, useRef, useState } from 'react'
import { useToasts } from 'react-toast-notifications'

import type { DoneUploadItem, PatientFilesUploadState } from '../PatientFilesUpload'
import { useUpdatePatientFileMutation } from '../patientFiles.generated'

export type RenameState = {
  fileID: string
  newFilename: string
  isLoading: boolean
  error?: string
}

type Props = {
  onChange: React.Dispatch<React.SetStateAction<PatientFilesUploadState>>
}

const useRenameFile = ({ onChange }: Props) => {
  const [updatePatientFile] = useUpdatePatientFileMutation()
  const { addToast } = useToasts()
  const [activeRename, setActiveRename] = useState<RenameState | null>(null)
  const renameInputRef = useRef<HTMLInputElement | null>(null)

  const renameFile = useCallback(
    async (item: DoneUploadItem) => {
      if (activeRename?.fileID !== item.file.fileID) return
      const newFilename = activeRename.newFilename.trim()
      if (!newFilename || newFilename === item.file.filename) {
        setActiveRename(null)
        return
      }

      setActiveRename((prev) => (prev ? { ...prev, newFilename, isLoading: true, error: undefined } : prev))
      try {
        const result = await updatePatientFile({
          variables: {
            fileID: item.file.fileID,
            input: { filename: newFilename },
          },
        })
        const updatedFile = result.data?.updatePatientFile
        if (!updatedFile) throw new Error('Failed to update filename')

        onChange((prev) =>
          prev.map((entry) =>
            entry.status === 'done' && entry.file.fileID === item.file.fileID ? { ...entry, file: updatedFile } : entry
          )
        )
        setActiveRename(null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to rename file'
        setActiveRename((prev) => (prev ? { ...prev, newFilename, isLoading: false, error: message } : prev))
      } finally {
        setActiveRename((prev) => (prev ? { ...prev, isLoading: false } : prev))
      }
    },
    [activeRename, onChange, updatePatientFile]
  )

  const handleRenameUnexpectedError = useCallback(
    (error: unknown) => {
      addToast(error instanceof Error ? error.message : 'Failed to rename file', {
        appearance: 'error',
      })
    },
    [addToast]
  )

  useEffect(() => {
    const timeoutID = activeRename
      ? window.setTimeout(() => {
          const input = renameInputRef.current
          if (!input) return
          const selectionEnd = getExtensionStartIndex(activeRename.newFilename)
          input.focus()
          input.setSelectionRange(0, selectionEnd)
        }, 0)
      : undefined

    return () => {
      if (timeoutID !== undefined) window.clearTimeout(timeoutID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRename?.fileID])

  return { activeRename, setActiveRename, renameFile, handleRenameUnexpectedError, renameInputRef }
}

function getExtensionStartIndex(filename: string): number {
  const dotIndex = filename.lastIndexOf('.')
  return dotIndex > 0 ? dotIndex : filename.length
}

export default useRenameFile
